import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CanvasComponentConfig } from 'types/canvas';
import {
  canvasTapeNewFiltersFamily,
  canvasTapeActiveCustomFilterFamily,
  canvasTapeFlowLiveFamily,
  canvasTapeColumnVisibilityFamily,
  canvasTapeColumnSortModelFamily,
  canvasTapeColumnOrderFamily,
  canvasTapeColumnSizesFamily,
  canvasTapeFilterActiveTabFamily,
  canvasTapeFilterPanelOpenFamily,
  canvasTapeActiveWatchlistIdsFamily,
} from 'states/canvasTape';
import { TapeCanvasStateSnapshot } from 'types/tape';

/**
 * Hydrates Canvas Tape atoms from DB config on mount, and syncs
 * atom changes back to Canvas DB via onConfigChange.
 */
export function useTapeCanvasSync(
  componentId: number,
  config: CanvasComponentConfig,
  onConfigChange: (patch: Partial<CanvasComponentConfig>) => void,
) {
  // --- Setters for hydration ---
  const setNewFilters = useSetRecoilState(
    canvasTapeNewFiltersFamily(componentId),
  );
  const setActiveCustomFilter = useSetRecoilState(
    canvasTapeActiveCustomFilterFamily(componentId),
  );
  const setFlowLive = useSetRecoilState(canvasTapeFlowLiveFamily(componentId));
  const setColumnVisibility = useSetRecoilState(
    canvasTapeColumnVisibilityFamily(componentId),
  );
  const setColumnSortModel = useSetRecoilState(
    canvasTapeColumnSortModelFamily(componentId),
  );
  const setColumnOrder = useSetRecoilState(
    canvasTapeColumnOrderFamily(componentId),
  );
  const setColumnSizes = useSetRecoilState(
    canvasTapeColumnSizesFamily(componentId),
  );
  const setFilterActiveTab = useSetRecoilState(
    canvasTapeFilterActiveTabFamily(componentId),
  );
  const setFilterPanelOpen = useSetRecoilState(
    canvasTapeFilterPanelOpenFamily(componentId),
  );
  const setActiveWatchlistIds = useSetRecoilState(
    canvasTapeActiveWatchlistIdsFamily(componentId),
  );

  // --- Hydrate from Canvas DB on mount ---
  const didHydrate = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    if (didHydrate.current) {
      return;
    }
    didHydrate.current = true;

    const stored = config.tapeState as TapeCanvasStateSnapshot | undefined;
    if (stored == null) {
      setIsHydrated(true);
      return;
    }

    if (stored.filters != null) {
      setNewFilters(stored.filters);
    }
    if (stored.activeCustomFilter != null) {
      setActiveCustomFilter(stored.activeCustomFilter);
    }
    if (stored.flowLive != null) {
      setFlowLive(stored.flowLive);
    }
    if (stored.columnVisibility != null) {
      setColumnVisibility(stored.columnVisibility);
    }
    if (stored.columnSortModel != null) {
      setColumnSortModel(stored.columnSortModel);
    }
    if (stored.columnOrder != null) {
      setColumnOrder(stored.columnOrder);
    }
    if (stored.columnSizes != null) {
      setColumnSizes(stored.columnSizes);
    }
    if (stored.filterActiveTab != null) {
      setFilterActiveTab(stored.filterActiveTab);
    }
    if (stored.filterPanelOpen != null) {
      setFilterPanelOpen(stored.filterPanelOpen);
    }
    if (stored.activeWatchlistIds != null) {
      setActiveWatchlistIds(stored.activeWatchlistIds);
    }
    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Read current values for sync ---
  const newFilters = useRecoilValue(canvasTapeNewFiltersFamily(componentId));
  const activeCustomFilter = useRecoilValue(
    canvasTapeActiveCustomFilterFamily(componentId),
  );
  const flowLive = useRecoilValue(canvasTapeFlowLiveFamily(componentId));
  const columnVisibility = useRecoilValue(
    canvasTapeColumnVisibilityFamily(componentId),
  );
  const columnSortModel = useRecoilValue(
    canvasTapeColumnSortModelFamily(componentId),
  );
  const columnOrder = useRecoilValue(canvasTapeColumnOrderFamily(componentId));
  const columnSizes = useRecoilValue(canvasTapeColumnSizesFamily(componentId));
  const filterActiveTab = useRecoilValue(
    canvasTapeFilterActiveTabFamily(componentId),
  );
  const filterPanelOpen = useRecoilValue(
    canvasTapeFilterPanelOpenFamily(componentId),
  );
  const activeWatchlistIds = useRecoilValue(
    canvasTapeActiveWatchlistIdsFamily(componentId),
  );

  const snapshot: TapeCanvasStateSnapshot = useMemo(
    () => ({
      filters: newFilters,
      activeCustomFilter,
      flowLive,
      columnVisibility,
      columnSortModel,
      columnOrder,
      columnSizes,
      filterActiveTab,
      filterPanelOpen,
      activeWatchlistIds,
    }),
    [
      newFilters,
      activeCustomFilter,
      flowLive,
      columnVisibility,
      columnSortModel,
      columnOrder,
      columnSizes,
      filterActiveTab,
      filterPanelOpen,
      activeWatchlistIds,
    ],
  );

  // --- Sync back to Canvas DB (skip first render to avoid overwriting) ---
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (isFirstSync.current) {
      isFirstSync.current = false;
      return;
    }
    onConfigChange({ tapeState: snapshot });
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isHydrated };
}
